import { User } from '../types/users';
import { Profile } from '../types/profiles';
import { Team } from '../types/teams';
import { api } from './api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthToken = () => {
  return sessionStorage.getItem('@KnowInDoc:accessToken');
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers: any = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export interface CreateUserRequest {
  customerId: string;
  userId: string;
  name: string;
  email: string;
  password: string;
  authenticationType: string;
  providerUserId: string;
  requireEmailConfirmation: boolean;
  isActive: boolean;
  roleId: string;
  cpf?: string | null;
  birthDate?: string | null;
}

export interface UpdateUserRequest {
  customerId?: string;
  userId?: string;
  name?: string;
  email?: string;
  password?: string;
  authenticationType?: string;
  providerUserId?: string;
  requireEmailConfirmation?: boolean;
  isActive?: boolean;
  roleId?: string;
  cpf?: string | null;
  birthDate?: string | null;
}

function normalizeBirthDateForInput(value: unknown): string {
  if (value == null || value === '')
  {
    return '';
  }

  if (typeof value === 'string')
  {
    return value.slice(0, 10);
  }

  if (typeof value === 'object' && value !== null && 'year' in value && 'month' in value && 'day' in value)
  {
    const dateValue = value as { year: number; month: number; day: number };
    const month = String(dateValue.month).padStart(2, '0');
    const day = String(dateValue.day).padStart(2, '0');
    return `${dateValue.year}-${month}-${day}`;
  }

  return String(value).slice(0, 10);
}

function normalizeUser(raw: unknown): User {
  const record = raw as Record<string, unknown>;
  const user = raw as unknown as User;
  const userIdentity = record.userIdentity ?? record.UserIdentity;
  const identityProvider =
    typeof userIdentity === 'object' &&
    userIdentity !== null &&
    ('provider' in userIdentity || 'Provider' in userIdentity)
      ? String((userIdentity as Record<string, unknown>).provider ?? (userIdentity as Record<string, unknown>).Provider ?? 'local')
      : undefined;

  const cpf = (record.cpf ?? record.Cpf) as string | null | undefined;
  const birthDateRaw = record.birthDate ?? record.BirthDate;

  return {
    ...user,
    cpf: cpf ?? null,
    birthDate: normalizeBirthDateForInput(birthDateRaw) || null,
    authenticationType: (record.authenticationType as string | undefined) ?? identityProvider ?? 'local',
  };
}

class UserService {
  // Buscar todos os usuários
  async getUsers(
    search: string = '',
    page: number = 1,
    pageSize: number = 10,
    roleId?: string,
    isActive?: boolean,
    customerId?: string
  ): Promise<{ users: User[]; total: number }> {
    const params: any = {
      search,
      page,
      pageSize
    };

    if (roleId) params.roleId = roleId;
    if (isActive !== undefined) {
      params.isActive = isActive;
    }
    if (customerId) {
      params.customerId = customerId;
    }

    try {
      // Usar o novo endpoint com filtros
      const response = await api.get('/users/filtered', { params });
      
      // Verificar se a resposta é um array direto ou objeto com propriedade users
      let users: User[];
      let total: number;
      
      if (Array.isArray(response.data)) {
        // Se for array direto
        users = response.data.map((user: unknown) => normalizeUser(user));
        total = response.data.length;
      } else if (response.data.users) {
        // Se for objeto com propriedade users
        users = response.data.users.map((user: unknown) => normalizeUser(user));
        total = response.data.total || response.data.users.length;
      } else {
        // Fallback
        users = [];
        total = 0;
      }
      
      return { users, total };
    } catch (error: any) {
      console.error('[userService.getUsers] Erro detalhado:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      });
      throw error;
    }
  }

  // Buscar usuário por ID
  async getUser(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return normalizeUser(response.data);
  }

  // Criar novo usuário
  async createUser(userData: CreateUserRequest, customerId?: string): Promise<User> {
    const payload = customerId
      ? { ...userData, customerId }
      : userData;
    const response = await api.post('/users', payload);
    return response.data;
  }

  // Atualizar usuário
  async updateUser(id: string, userData: UpdateUserRequest): Promise<User> {
    const response = await api.put(`/users/${id}`, userData);
    return normalizeUser(response.data);
  }

  // Excluir usuário
  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  }

  // Buscar perfis disponíveis
  async getProfiles(customerId?: string): Promise<Profile[]> {
    const response = await api.get('/roles', {
      params: customerId ? { customerId } : undefined,
    });
    return response.data;
  }

  // Buscar equipes disponíveis
  async getTeams(): Promise<Team[]> {
    const response = await api.get('/teams');
    return response.data;
  }

  // Métodos existentes para compatibilidade
  async getUsersByRole(roleId: string, search: string = '', page: number = 1, pageSize: number = 10): Promise<{ users: User[]; total: number }> {
    const response = await api.get('/users', {
      params: {
        roleId,
        search,
        page,
        pageSize
      }
    });
    return response.data;
  }

  async getAvailableUsers(roleId: string, search: string = ''): Promise<{ users: User[] }> {
    const response = await api.get('/users/available', {
      params: {
        roleId,
        search
      }
    });
    return response.data;
  }

  async addUsersToRole(roleId: string, userIds: string[]): Promise<void> {
    await api.post(`/roles/${roleId}/users`, { userIds });
  }
}

// Instância da classe para métodos de CRUD
const userServiceInstance = new UserService();

// Objeto exportado com todos os métodos
export const userService = {
  // Métodos de CRUD (da classe)
  getUsers: (search?: string, page?: number, pageSize?: number, roleId?: string, isActive?: boolean, customerId?: string) =>
    userServiceInstance.getUsers(search, page, pageSize, roleId, isActive, customerId),
  getUser: userServiceInstance.getUser.bind(userServiceInstance),
  createUser: (userData: CreateUserRequest, customerId?: string) =>
    userServiceInstance.createUser(userData, customerId),
  updateUser: userServiceInstance.updateUser.bind(userServiceInstance),
  deleteUser: userServiceInstance.deleteUser.bind(userServiceInstance),
  getProfiles: (customerId?: string) => userServiceInstance.getProfiles(customerId),
  getTeams: userServiceInstance.getTeams.bind(userServiceInstance),
  getUsersByRole: userServiceInstance.getUsersByRole.bind(userServiceInstance),
  getAvailableUsers: userServiceInstance.getAvailableUsers.bind(userServiceInstance),
  addUsersToRole: userServiceInstance.addUsersToRole.bind(userServiceInstance),

  // Métodos de perfil (novos)
  async updateProfile(formData: FormData): Promise<void> {
    try {
      const response = await axios.put(`${API_URL}/users/profile`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Erro detalhado ao atualizar perfil:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      throw new Error(error.response?.data?.message || 'Erro ao atualizar perfil');
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      
      // Obter dados do usuário atual
      const userData = sessionStorage.getItem('@KnowInDoc:user');
      const user = userData ? JSON.parse(userData) : null;
      const customerId = sessionStorage.getItem('@KnowInDoc:customer_id');
      
      if (!user || !customerId) {
        throw new Error('Dados do usuário não encontrados');
      }
      
      // Criar FormData para senha
      const formData = new FormData();
      formData.append('Name', user.name);
      formData.append('Password', newPassword);
      
      const response = await axios.put(`${API_URL}/users/profile`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      throw new Error(error.response?.data?.message || 'Erro ao alterar senha');
    }
  },

  // Método auxiliar para converter arquivo para base64 (mantido para compatibilidade)
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remover o prefixo "data:image/jpeg;base64," para obter apenas o base64
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => {
        console.error('Erro ao converter arquivo:', error);
        reject(error);
      };
    });
  }
}; 